import { React, useState, useContext, useEffect } from "react";
import { motion } from "framer-motion";
import Preloader from "../components/Preloader"; // Adjust path
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { AuthContext } from "../context/AuthContext"; // Adjust path
import BackButton from "../includes/Back_btn"; // Adjust path
import styles from '../assets/css/GamePageLayout.module.css'; // Import NEW CSS Module
import { BiTimeFive } from "react-icons/bi"; // Timer icon

// Helper function to format time (HH:MM:SS)
const formatTime = (timeString) => {
    if (!timeString || timeString === "00:00:00") return "00:00:00";
    // Ensure padding (though calculation function should handle it)
    return String(timeString).split(':').map(part => String(part).padStart(2, '0')).join(':');
};

// Main Layout Component
const GamePageLayout = ({ children, handlers, id, GameName: initialGameName, new_message }) => {
  const [GameName, setGameName] = useState(initialGameName || "Loading...");
  const [preloader, setPreloader] = useState(!initialGameName); // Show preloader if name isn't passed
  const { isAuthenticated, Gameinfo } = useContext(AuthContext);
  const [timeLeft, setTimeLeft] = useState("00:00:00");
  const [motiJodiTimeLeft, setMotiJodiTimeLeft] = useState("00:00:00");
  const [targetDateTime, setTargetDateTime] = useState(""); // End time for main game
  const [targetDateTimeMotiJodi, setTargetDateTimeMotiJodi] = useState(""); // End time for Moti Jodi

  const navigate = useNavigate();

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, navigate]);

  // Toast message function
  const message = (status, text) => {
    const options = {
        position: "top-right", autoClose: 3000, hideProgressBar: false,
        closeOnClick: true, pauseOnHover: true, draggable: true,
        progress: undefined, theme: "dark",
    };
    if (status === 1) toast.success(text, options);
    else if (status === 0) toast.error(text, options);
    else if (status === 2) toast.warning(text, options);
  };

  // Display new_message prop via toast
  useEffect(() => {
      if (new_message) {
          // Assuming new_message structure might be { status: 1, msg: "..." } or just a string
          if (typeof new_message === 'object' && new_message.msg) {
              message(new_message.status ?? 1, new_message.msg);
          } else if (typeof new_message === 'string') {
              message(1, new_message); // Default to success if it's just a string
          }
      }
  }, [new_message]);


  // Fetch Game Info if not passed via props or if id changes
  useEffect(() => {
    let isMounted = true;
    if (Gameinfo && id) {
        setPreloader(true);
        Gameinfo(id, (res) => {
            if (!isMounted) return;
            setPreloader(false);
            if (res.status === 0) {
                message(0, res.msg || `Failed to load game info for ID: ${id}`);
                setGameName("Unknown Game");
            } else if (res.data && res.data.length > 0) {
                const daa = res.data[0];
                setGameName(daa.game_name || "Unknown Game");
                setTargetDateTime(daa.end_date && daa.end_time ? `${daa.end_date} ${daa.end_time}` : "");
                setTargetDateTimeMotiJodi(daa.result_date && daa.result_time ? `${daa.result_date} ${daa.result_time}` : "");
            } else {
                setGameName("Unknown Game");
            }
        });
    } else if (!initialGameName) {
         setGameName("Unknown Game");
         setPreloader(false);
    } else {
         setPreloader(false);
    }
    return () => { isMounted = false; };
  }, [id, Gameinfo, initialGameName]);

  // Timer Calculation Effect
  useEffect(() => {
    const calculateTimeLeft = (targetDateTimeString) => {
        if (!targetDateTimeString) return "00:00:00";
        try {
            const targetDate = new Date(targetDateTimeString);
            // Check if targetDate is valid
            if (isNaN(targetDate.getTime())) {
                // console.warn("Invalid target date string:", targetDateTimeString);
                return "00:00:00";
            }
            const now = new Date();
            const difference = targetDate - now;

            if (difference > 0) {
                const hours = String(Math.floor(difference / (1000 * 60 * 60))).padStart(2, '0');
                const minutes = String(Math.floor((difference / (1000 * 60)) % 60)).padStart(2, '0');
                const seconds = String(Math.floor((difference / 1000) % 60)).padStart(2, '0');
                return `${hours}:${minutes}:${seconds}`;
            } else {
                return "00:00:00"; // Time has passed
            }
        } catch (error) {
            console.error("Error calculating time left:", error);
            return "00:00:00";
        }
    };

    // Initial calculation
    setTimeLeft(calculateTimeLeft(targetDateTime));
    setMotiJodiTimeLeft(calculateTimeLeft(targetDateTimeMotiJodi));

    // Update every second
    const interval = setInterval(() => {
        setTimeLeft(calculateTimeLeft(targetDateTime));
        setMotiJodiTimeLeft(calculateTimeLeft(targetDateTimeMotiJodi));
    }, 1000);

    // Cleanup interval on component unmount or when target times change
    return () => clearInterval(interval);
  }, [targetDateTime, targetDateTimeMotiJodi]);

  // Framer Motion variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };
  const itemVariants = {
      hidden: { y: -10, opacity: 0 },
      visible: { y: 0, opacity: 1, transition: { duration: 0.3 } },
  };

  return (
    <>
      <Preloader status={preloader} />
      <ToastContainer position="top-right" autoClose={3000} theme="dark" />

      {/* Use the Aghori theme container */}
      <div className={styles.aghoriLayoutContainer}>
        <motion.div
            {...handlers} // Apply swipe handlers here
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit={{ opacity: 0 }}
        >
            {/* --- Header --- */}
            <motion.div className={styles.aghoriGameHeader} variants={itemVariants}>
                <BackButton className={styles.backButton} />
                <p className={styles.gameName}>{GameName}</p>
                <div className={styles.timerContainer}>
                    <p className={styles.timerLabel}>Ends In</p>
                    <p className={styles.timerValue}><BiTimeFive /> {formatTime(timeLeft)}</p>
                </div>
            </motion.div>

            {/* --- Moti Jodi Message Bar --- */}
            {targetDateTimeMotiJodi && motiJodiTimeLeft !== "00:00:00" && ( // Only show if time is available and hasn't passed
                 <motion.div className={styles.motiJodiBar} variants={itemVariants}>
                    Moti Jodi Result In : {formatTime(motiJodiTimeLeft)}
                 </motion.div>
            )}

            {/* --- Render Child Component (e.g., Playground content) --- */}
            <div className={styles.gameContent}>
                {children}
            </div>

        </motion.div>
      </div>
       {/* Footer is rendered by the child page (e.g., Playground) */}
    </>
  );
};

export default GamePageLayout;
